const SUBSCRIPTION_STORAGE_KEY = "njoy-push-subscription";
const LEGACY_FCM_TOKEN_STORAGE_KEY = "njoy-fcm-token";
const DEVICE_ID_STORAGE_KEY = "njoy-web-push-device-id";
const PUSH_OPTOUT_STORAGE_KEY = "njoy-push-opted-out";
const PUSH_PROMPT_SHOWN_STORAGE_KEY = "njoy-push-first-login-prompted";
const FCM_PROVIDER = "firebase-cloud-messaging";
const WEB_PUSH_PROVIDER = "web-push";
const PUSH_PROMPT_NEEDED_EVENT = "njoy:push-prompt-needed";

type PushSubscriptionProvider =
  | "firebase-cloud-messaging"
  | "fcm"
  | "web-push"
  | "webpush";

interface StoredPushSubscription {
  provider: string;
  token: string;
}

interface RegisterHandlers {
  registerPushSubscription: (input: {
    provider: PushSubscriptionProvider;
    token: string;
    platform?: string;
    deviceId?: string;
  }) => Promise<{ errors: Record<string, string> | null; message?: string }>;
}

interface UnregisterHandlers {
  unregisterPushSubscription: (
    provider: PushSubscriptionProvider,
    token: string,
  ) => Promise<{ errors: Record<string, string> | null; message?: string }>;
}

interface DisableAllHandlers {
  disableMyPushSubscriptions?: (provider?: PushSubscriptionProvider) => Promise<{
    errors: Record<string, string> | null;
    message?: string;
  }>;
}

const toSupportedProvider = (provider: string): PushSubscriptionProvider | null => {
  const normalized = String(provider || "").trim().toLowerCase();

  if (normalized === "firebase-cloud-messaging" || normalized === "fcm") {
    return "firebase-cloud-messaging";
  }

  if (normalized === "web-push" || normalized === "webpush") {
    return "web-push";
  }

  return null;
};

export interface PushNotificationHealth {
  isSupported: boolean;
  hasFirebaseConfig: boolean;
  permission: NotificationPermission | "unsupported";
  isOptedOut: boolean;
  hasStoredToken: boolean;
  hasServiceWorkerSupport: boolean;
  hasServiceWorkerController: boolean;
  hasActiveServiceWorker: boolean;
}

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY || "",
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || "",
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || "",
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || "",
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || "",
  appId: process.env.REACT_APP_FIREBASE_APP_ID || "",
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID || "",
};

const vapidKey = process.env.REACT_APP_FIREBASE_VAPID_KEY || "";
const webPushVapidPublicKey = process.env.REACT_APP_WEB_PUSH_VAPID_PUBLIC_KEY || vapidKey;
const pushDebugEnabled = (process.env.REACT_APP_PUSH_DEBUG || "").toLowerCase() === "true";

const waitForServiceWorkerReady = async (timeoutMs = 10000) => {
  if (typeof window === "undefined" || !("serviceWorker" in navigator)) {
    return null;
  }

  const timeout = new Promise<null>((resolve) => {
    window.setTimeout(() => resolve(null), timeoutMs);
  });

  const ready = navigator.serviceWorker.ready
    .then((registration) => registration)
    .catch(() => null);

  return Promise.race([ready, timeout]);
};

const hasFirebaseConfig = () => {
  return Boolean(
    firebaseConfig.apiKey &&
      firebaseConfig.projectId &&
      firebaseConfig.messagingSenderId &&
      firebaseConfig.appId &&
      vapidKey,
  );
};

const hasWebPushConfig = () => {
  return Boolean(webPushVapidPublicKey);
};

const getStoredSubscription = (): StoredPushSubscription | null => {
  if (typeof window === "undefined") {
    return null;
  }

  const raw = window.localStorage.getItem(SUBSCRIPTION_STORAGE_KEY);
  if (raw) {
    try {
      const parsed = JSON.parse(raw) as StoredPushSubscription;
      if (parsed && typeof parsed.provider === "string" && typeof parsed.token === "string") {
        const provider = parsed.provider.trim();
        const token = parsed.token.trim();
        if (provider && token) {
          return { provider, token };
        }
      }
    } catch {
      // ignore malformed storage values and fall back to legacy key
    }
  }

  const legacyToken = String(window.localStorage.getItem(LEGACY_FCM_TOKEN_STORAGE_KEY) || "").trim();
  if (legacyToken) {
    return {
      provider: FCM_PROVIDER,
      token: legacyToken,
    };
  }

  return null;
};

const setPushOptedOut = (value: boolean) => {
  if (typeof window === "undefined") {
    return;
  }

  if (value) {
    window.localStorage.setItem(PUSH_OPTOUT_STORAGE_KEY, "1");
    return;
  }

  window.localStorage.removeItem(PUSH_OPTOUT_STORAGE_KEY);
};

export const isPushOptedOut = () => {
  if (typeof window === "undefined") {
    return false;
  }

  return window.localStorage.getItem(PUSH_OPTOUT_STORAGE_KEY) === "1";
};

export const hasShownFirstLoginPushPrompt = (): boolean => {
  if (typeof window === "undefined") {
    return false;
  }

  return window.localStorage.getItem(PUSH_PROMPT_SHOWN_STORAGE_KEY) === "1";
};

export const markFirstLoginPushPromptShown = (): void => {
  if (typeof window === "undefined") {
    return;
  }

  window.localStorage.setItem(PUSH_PROMPT_SHOWN_STORAGE_KEY, "1");
};

export const requestPushPromptForAction = (reason: string): void => {
  if (typeof window === "undefined") {
    return;
  }

  window.dispatchEvent(
    new CustomEvent(PUSH_PROMPT_NEEDED_EVENT, {
      detail: { reason },
    }),
  );
};

export const pushPromptNeededEventName = PUSH_PROMPT_NEEDED_EVENT;

export const getPushNotificationHealth = async (): Promise<PushNotificationHealth> => {
  const isSupported = typeof window !== "undefined" && "Notification" in window;
  const permission = isSupported ? Notification.permission : "unsupported";
  const hasServiceWorkerSupport = typeof window !== "undefined" && "serviceWorker" in navigator;

  let hasActiveServiceWorker = false;
  if (hasServiceWorkerSupport) {
    try {
      const registration = await navigator.serviceWorker.getRegistration("/");
      hasActiveServiceWorker = Boolean(registration?.active);
    } catch {
      hasActiveServiceWorker = false;
    }
  }

  return {
    isSupported,
    hasFirebaseConfig: hasFirebaseConfig(),
    permission,
    isOptedOut: isPushOptedOut(),
    hasStoredToken: Boolean(getStoredSubscription()),
    hasServiceWorkerSupport,
    hasServiceWorkerController:
      hasServiceWorkerSupport && Boolean(navigator.serviceWorker.controller),
    hasActiveServiceWorker,
  };
};

const setStoredSubscription = (subscription: StoredPushSubscription | null) => {
  if (typeof window === "undefined") {
    return;
  }

  if (!subscription) {
    window.localStorage.removeItem(SUBSCRIPTION_STORAGE_KEY);
    window.localStorage.removeItem(LEGACY_FCM_TOKEN_STORAGE_KEY);
    return;
  }

  window.localStorage.setItem(SUBSCRIPTION_STORAGE_KEY, JSON.stringify(subscription));

  if (subscription.provider === FCM_PROVIDER) {
    window.localStorage.setItem(LEGACY_FCM_TOKEN_STORAGE_KEY, subscription.token);
  } else {
    window.localStorage.removeItem(LEGACY_FCM_TOKEN_STORAGE_KEY);
  }
};

const getDeviceId = () => {
  if (typeof window === "undefined") {
    return undefined;
  }

  const existing = window.localStorage.getItem(DEVICE_ID_STORAGE_KEY);
  if (existing) {
    return existing;
  }

  const nextId = typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `web-${Date.now()}-${Math.random().toString(36).slice(2)}`;

  window.localStorage.setItem(DEVICE_ID_STORAGE_KEY, nextId);
  return nextId;
};

const getFirebaseMessaging = async () => {
  if (typeof window === "undefined" || !window.firebase || !hasFirebaseConfig()) {
    return null;
  }

  if (!window.firebase.apps || window.firebase.apps.length === 0) {
    window.firebase.initializeApp(firebaseConfig);
  }

  return window.firebase.messaging();
};

const getServiceWorkerRegistration = async () => {
  if (typeof window === "undefined" || !("serviceWorker" in navigator)) {
    return null;
  }

  const params = new URLSearchParams();
  if (hasFirebaseConfig()) {
    params.set("apiKey", firebaseConfig.apiKey);
    params.set("authDomain", firebaseConfig.authDomain);
    params.set("projectId", firebaseConfig.projectId);
    params.set("storageBucket", firebaseConfig.storageBucket);
    params.set("messagingSenderId", firebaseConfig.messagingSenderId);
    params.set("appId", firebaseConfig.appId);
    params.set("measurementId", firebaseConfig.measurementId);
  }

  if (pushDebugEnabled) {
    params.set("pushDebug", "1");
  }

  try {
    const registration = await navigator.serviceWorker.register(
      `/firebase-messaging-sw.js${params.toString() ? `?${params.toString()}` : ""}`,
    );

    if (registration.active) {
      return registration;
    }

    const readyRegistration = await waitForServiceWorkerReady();
    return readyRegistration ?? registration;
  } catch {
    return null;
  }
};

const urlBase64ToArrayBuffer = (base64String: string): ArrayBuffer => {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const buffer = new ArrayBuffer(rawData.length);
  const outputArray = new Uint8Array(buffer);

  for (let index = 0; index < rawData.length; index += 1) {
    outputArray[index] = rawData.charCodeAt(index);
  }

  return buffer;
};

const getWebPushSubscriptionToken = async () => {
  if (
    typeof window === "undefined" ||
    !("Notification" in window) ||
    !("serviceWorker" in navigator) ||
    !("PushManager" in window) ||
    !hasWebPushConfig()
  ) {
    return null;
  }

  if (Notification.permission !== "granted") {
    return null;
  }

  const serviceWorkerRegistration = await getServiceWorkerRegistration();
  if (!serviceWorkerRegistration || !serviceWorkerRegistration.active) {
    return null;
  }

  try {
    const existingSubscription = await serviceWorkerRegistration.pushManager.getSubscription();
    if (existingSubscription) {
      return JSON.stringify(existingSubscription.toJSON());
    }

    const subscription = await serviceWorkerRegistration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToArrayBuffer(webPushVapidPublicKey),
    });

    return JSON.stringify(subscription.toJSON());
  } catch {
    return null;
  }
};

const getBrowserPushToken = async () => {
  if (typeof window === "undefined" || !("Notification" in window)) {
    return null;
  }

  if (Notification.permission !== "granted") {
    return null;
  }

  const messaging = await getFirebaseMessaging();
  const serviceWorkerRegistration = await getServiceWorkerRegistration();
  if (!messaging || !serviceWorkerRegistration || !serviceWorkerRegistration.active) {
    return null;
  }

  try {
    return await messaging.getToken({
      vapidKey,
      serviceWorkerRegistration,
    });
  } catch {
    return null;
  }
};

export const syncBrowserPushSubscription = async ({
  registerPushSubscription,
  unregisterPushSubscription,
}: RegisterHandlers & UnregisterHandlers) => {
  if (!hasFirebaseConfig() && !hasWebPushConfig()) {
    return { synced: false, reason: "missing-config" };
  }

  const previous = getStoredSubscription();

  const registerAndPersist = async (provider: PushSubscriptionProvider, token: string) => {
    if (previous && (previous.provider !== provider || previous.token !== token)) {
      const previousProvider = toSupportedProvider(previous.provider);
      if (previousProvider) {
        await unregisterPushSubscription(previousProvider, previous.token);
      }
    }

    if (previous && previous.provider === provider && previous.token === token) {
      return { synced: true, reason: "already-synced" as const };
    }

    const result = await registerPushSubscription({
      provider,
      token,
      platform: "web",
      deviceId: getDeviceId(),
    });

    if (!result.errors) {
      setStoredSubscription({ provider, token });
      return { synced: true, reason: undefined };
    }

    return { synced: false, reason: "register-failed" as const };
  };

  const fcmToken = hasFirebaseConfig() ? await getBrowserPushToken() : null;
  if (fcmToken) {
    return registerAndPersist(FCM_PROVIDER, fcmToken);
  }

  const webPushToken = await getWebPushSubscriptionToken();
  if (webPushToken) {
    return registerAndPersist(WEB_PUSH_PROVIDER, webPushToken);
  }

  return { synced: false, reason: "missing-token" };
};

export const enableBrowserPush = async (handlers: RegisterHandlers & UnregisterHandlers) => {
  if (typeof window === "undefined" || !("Notification" in window)) {
    return { enabled: false, reason: "unsupported" };
  }

  if (!hasFirebaseConfig() && !hasWebPushConfig()) {
    return { enabled: false, reason: "missing-config" };
  }

  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    return { enabled: false, reason: permission };
  }

  const synced = await syncBrowserPushSubscription(handlers);
  if (synced.synced) {
    setPushOptedOut(false);
  }

  return { enabled: synced.synced, reason: synced.reason };
};

export const disableBrowserPush = async ({
  unregisterPushSubscription,
  disableMyPushSubscriptions,
  persistOptOut = true,
}: UnregisterHandlers & DisableAllHandlers & { persistOptOut?: boolean }) => {
  if (persistOptOut) {
    setPushOptedOut(true);
  }

  const storedSubscription = getStoredSubscription();

  let tokenDisableResult = true;
  if (storedSubscription) {
    const provider = toSupportedProvider(storedSubscription.provider);
    if (!provider) {
      tokenDisableResult = false;
    } else {
      const result = await unregisterPushSubscription(provider, storedSubscription.token);
      tokenDisableResult = !result.errors;
    }
  }

  let disableAllResult = true;
  if (disableMyPushSubscriptions) {
    const result = await disableMyPushSubscriptions();
    disableAllResult = !result.errors;
  }

  const disabled = tokenDisableResult && disableAllResult;

  if (disabled) {
    setStoredSubscription(null);
  }

  return { disabled };
};